// onboarding.menu.* (ops plan 2026-10-14-staff-agents, doc 18 "Menu build playbook" and "Live sessions"; lane O):
// Radish QA publishes a read-only snapshot of a new store's draft menu for the merchant's review link, and the merchant
// taps Approve there. Only the review link's principal (audience `review_link`, minted by the live-session service for
// one location and one link) records an approval: never Launchpad, the agent or a staff token (coordinator, 2026-10-15).
// `catalog.applyMenuBundle` then writes only an approved fingerprint. `onboarding` is a sub-namespace of `ops`.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { secret } from '../../define/secret.ts';
import { IsoInstant, LocationId, Money, ProductId, Rate, VariantId } from '../../primitives/codecs.ts';
import { DiscountRuleFields, DiscountSchedule, DiscountValue } from '../catalog/index.ts';
import { CatalogInvalid } from '../catalog/errors.ts';
import { PRODUCT_IMAGE_BODY_BYTES, ProductImageData, ProductImageInvalid, ProductImageStoreUnavailable, ProductImageType } from '../catalog/images.ts';
import {
  MenuBundle, MenuBundleDiff, MenuDigest, MenuEntityKind, MenuPreviewId, MenuRemovalKind, MenuReference, MenuTaxProfile, OnboardingPreviewNotFound, OnboardingPreviewStale,
  OnboardingReferencesUnmatched,
} from '../catalog/menu-bundle.ts';

const PREVIEW_CLOUD = "The review link is served from the cloud, which keeps every published preview and the merchant's approval.";


/** The review link is expired, revoked, for another purpose or for another location. */
export const OnboardingLinkInvalid = defineError({
  code: 'onboarding.link_invalid', httpStatus: 403, message: 'errors.onboarding.link_invalid', retryable: false,
  details: z.object({}),
});

/** An approval reached through the Broker: only the rep's own Launchpad, from its screen, may record one. */
export const OnboardingApprovalByAgent = defineError({
  code: 'onboarding.approval_by_agent', httpStatus: 403, message: 'errors.onboarding.approval_by_agent', retryable: false,
  details: z.object({}),
});

/** An in-person approval needs the rep's own user session as the witness: a device, a store employee or the system can't. */
export const OnboardingApprovalNeedsRep = defineError({
  code: 'onboarding.approval_needs_rep', httpStatus: 403, message: 'errors.onboarding.approval_needs_rep', retryable: false,
  details: z.object({}),
});

// The share link's refusals (core `shareApprovalAllowed`, lane LP2), one to one: the link is over; the rep isn't sharing
// now; the menu on screen is no longer the one signed off; the key isn't this menu's; the key was used already.
export const OnboardingShareEnded = defineError({ code: 'onboarding.share_ended', httpStatus: 410, message: 'errors.onboarding.share_ended', retryable: false, details: z.object({}) });
export const OnboardingNotSharing = defineError({ code: 'onboarding.not_sharing', httpStatus: 409, message: 'errors.onboarding.not_sharing', retryable: true, details: z.object({}) });
export const OnboardingMenuChanged = defineError({ code: 'onboarding.menu_changed', httpStatus: 409, message: 'errors.onboarding.menu_changed', retryable: false, details: z.object({}) });
export const OnboardingKeyWrong = defineError({ code: 'onboarding.key_wrong', httpStatus: 403, message: 'errors.onboarding.key_wrong', retryable: false, details: z.object({}) });
export const OnboardingKeyUsed = defineError({ code: 'onboarding.key_used', httpStatus: 409, message: 'errors.onboarding.key_used', retryable: false, details: z.object({}) });
/** More sign-off attempts on one share in a minute than core SHARE_RULES.maxApprovalAttemptsPerMinute: refused unaudited. */
export const OnboardingTooManyAttempts = defineError({ code: 'onboarding.too_many_attempts', httpStatus: 429, message: 'errors.onboarding.too_many_attempts', retryable: true, details: z.object({ perMinute: z.int() }) });

const menuBuild = { aggregate: 'config', merge: 'lifecycle', deviceConsumed: false } as const;

export const MenuPreviewPublished = defineEvent({
  ...menuBuild,
  name: 'MenuPreviewPublished',
  version: 1,
  summary: "A new revision of a location's draft menu was published for the merchant's review link.",
  payload: z.object({ previewId: MenuPreviewId, locationId: LocationId, revision: z.int().positive(), fingerprint: MenuDigest, at: IsoInstant }),
  verifiable: ['previewId', 'revision', 'fingerprint'],
});

/** The review a v2 approval came from (the cloud preview and its review link); null on every approval since v3. */
const LegacyReview = z.object({
  previewId: MenuPreviewId,
  revision: z.int().positive(),
  /** What the approved revision removes from the location, as the review page listed it. */
  removals: z.array(z.object({ kind: MenuRemovalKind, id: z.uuid(), action: z.enum(['archive', 'delete']) })),
  sessionLinkId: z.uuid(),
});

type MenuApprovedV2 = {
  approvalId: string; locationId: string; previewId: string; revision: number; fingerprint: string;
  removals: z.input<typeof LegacyReview>['removals']; approverName: string; sessionLinkId: string; callId: string | null; at: string;
};

export const MenuApproved = defineEvent({
  ...menuBuild,
  name: 'MenuApproved',
  // v2: a removal may be a product's image (menu bundle v2); every v1 removal is one of the kinds v2 still has.
  // v3 (Launchpad, ops plan 2026-10-17-launchpad-menu-build §4.7): the client approves the menu and layout they see,
  // by `menuApprovalFingerprint`, through the share link or in person beside the rep (the witness). The menu stays on
  // the rep's computer until Send, so v3 names no preview; what comes off is named at Send. A v2 approval reads as a
  // review-link approval of a bundle fingerprint, its preview kept in `legacyReview`.
  version: 3,
  upcastFrom: [
    { version: 1, up: (old) => old as never },
    {
      version: 2,
      up: (old) => {
        const v2 = old as MenuApprovedV2;
        return {
          approvalId: v2.approvalId, locationId: v2.locationId, fingerprint: v2.fingerprint, fingerprintKind: 'bundle', how: 'link', approverName: v2.approverName,
          witness: null, shareId: null, callId: v2.callId, at: v2.at,
          legacyReview: { previewId: v2.previewId, revision: v2.revision, removals: v2.removals, sessionLinkId: v2.sessionLinkId },
        };
      },
    },
  ],
  summary: 'The client approved one exact version of their menu: through the link they watched, or in person beside the rep.',
  payload: z.object({
    approvalId: z.uuid(),
    locationId: LocationId,
    fingerprint: MenuDigest,
    /** `approval`: core `menuApprovalFingerprint` (menu and layout, v3); `bundle`: the bundle's own (a v2 review link). */
    fingerprintKind: z.enum(['approval', 'bundle']),
    how: z.enum(['link', 'in_person']),
    /** The name the client typed. The number a link was sent to stays on the share's record. */
    approverName: z.string(),
    /**
     * The rep who shared the link, or beside whom the client approved in person: a Radish staff member's own user (an
     * actor of kind `user`; `staff` is a store employee). Null on a v2 review-link approval.
     */
    witness: z.object({ kind: z.literal('user'), id: z.uuid() }).nullable(),
    /** The share the client approved through (how: link, v3). */
    shareId: z.uuid().nullable(),
    /** The Dialpad call the review happened on, when there was one. */
    callId: z.string().nullable(),
    at: IsoInstant,
    legacyReview: LegacyReview.nullable(),
  }),
  verifiable: ['fingerprint', 'how'],
  pii: ['approverName'],
});

export const PublishMenuPreview = defineCommand({
  name: 'onboarding.menu.publishPreview',
  summary:
    "Publish the staff member's local draft menu as the location's review snapshot: a new revision, its fingerprint, and the diff against the location now (removals listed apart), which the review link shows live.",
  input: z.object({
    previewId: MenuPreviewId,
    locationId: LocationId,
    bundle: MenuBundle,
    /** The local rows the draft names; each is mapped to the store's production row before the bundle is stored. */
    references: z.array(MenuReference).max(2000).optional(),
  }),
  output: z.object({
    previewId: MenuPreviewId, revision: z.int().positive(), fingerprint: MenuDigest, diff: MenuBundleDiff, publishedAt: IsoInstant,
    /** The production tax profiles the stored bundle names, so the review shows each item's tax by name. */
    taxProfiles: z.array(MenuTaxProfile).default([]),
  }),
  permission: 'onboarding.menu.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: PREVIEW_CLOUD,
  emits: [MenuPreviewPublished],
  errors: [CatalogInvalid, OnboardingReferencesUnmatched],
  dryRun: false,
  maxBodyBytes: 4 * 1024 * 1024,
  agent: { expose: true, surfaces: ['staff.onboarding'] },
});

export const RecordMenuApproval = defineCommand({
  name: 'onboarding.menu.recordApproval',
  summary:
    "The client's sign-off through their link: the menu the rep is sharing with them live (Launchpad's share), or a published review preview (the older review link). Records who signed off on which exact menu, on which link and call.",
  input: z.object({
    /** Must be the one location the link is for. */
    locationId: LocationId,
    /** On a share: core `menuApprovalFingerprint` of the live menu and layout on screen. On a review link: the preview revision's. */
    fingerprint: MenuDigest,
    approverName: z.string().trim().min(1).max(120),
    /** A review link's preview and revision (the older path); never sent on a share. */
    previewId: MenuPreviewId.optional(),
    revision: z.int().positive().optional(),
    /** The share the client is watching: the one its link's token names (ops plan 2026-10-17-launchpad-share). */
    shareId: z.uuid().optional(),
    /** The one-time key the share's page was given for the menu on screen (22 base64url characters). Never stored. */
    approvalKey: secret(z.string().regex(/^[A-Za-z0-9_-]{22}$/)).optional(),
  }),
  output: z.object({ approvalId: z.uuid(), approvedAt: IsoInstant }),
  // The link's own principal, scoped to one location and one link; no other operation admits it.
  permission: { session: 'review_link' },
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: PREVIEW_CLOUD,
  emits: [MenuApproved],
  errors: [OnboardingLinkInvalid, OnboardingPreviewStale, OnboardingPreviewNotFound, OnboardingShareEnded, OnboardingNotSharing, OnboardingMenuChanged, OnboardingKeyWrong, OnboardingKeyUsed, OnboardingTooManyAttempts],
  dryRun: true,
  agent: { expose: false },
});

/** The client's Approve given in person, on the rep's Launchpad (ops plan 2026-10-17-launchpad-menu-build, Decided A109 (a)). */
export const RecordInPersonMenuApproval = defineCommand({
  name: 'onboarding.menu.recordInPersonApproval',
  summary:
    "The client's Approve given in person on the rep's Launchpad: records who approved which exact menu and layout, with the rep as the witness.",
  input: z.object({
    locationId: LocationId,
    /** Core `menuApprovalFingerprint` of the menu and layout on the rep's screen. */
    fingerprint: MenuDigest,
    approverName: z.string().trim().min(1).max(120),
  }),
  output: z.object({ approvalId: z.uuid(), approvedAt: IsoInstant }),
  permission: 'onboarding.menu.manage',
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The approval is the cloud record that Send checks; nothing about the menu is sent with it.',
  emits: [MenuApproved],
  errors: [OnboardingApprovalByAgent, OnboardingApprovalNeedsRep],
  dryRun: true,
  // Claude has no tool that approves (plan: "Claude has no tool that shares, approves or sends"): never on an agent
  // surface, and the handler refuses any call made through the Broker.
  agent: { expose: false },
});

/**
 * What the merchant sees on the review link: their menu as the newest revision has it, what it removes from the store,
 * and its taxes by name. Nothing staff-internal: no publisher, no staff names, no other location (coordinator, 2026-10-15).
 */
export const MenuPreviewForReview = z
  .object({
    previewId: MenuPreviewId,
    locationId: LocationId,
    revision: z.int().positive(),
    fingerprint: MenuDigest,
    publishedAt: IsoInstant,
    menu: MenuBundle,
    removals: z.array(z.object({ kind: MenuRemovalKind, name: z.string(), action: z.enum(['archive', 'delete']) })),
    taxProfiles: z.array(MenuTaxProfile),
    /** The items the menu shows a photo for (one the bundle sets, or the store's own it keeps): read each with onboarding.menu.previewImage. */
    images: z.array(z.object({ productId: z.uuid(), sha256: z.string() })),
    /** Taxes this menu adds to the store, by name and exact rate ("New tax: City Tax 2.5%"). */
    newTaxes: z.array(z.object({ name: z.string(), rate: Rate })),
    /** Discounts this menu adds, with every field that changes what a customer pays. */
    newDiscounts: z.array(z.object({
      name: z.string(),
      application: DiscountRuleFields.shape.application,
      adjustmentKind: DiscountRuleFields.shape.adjustmentKind,
      discountScope: DiscountRuleFields.shape.discountScope,
      value: DiscountValue,
      combinability: DiscountRuleFields.shape.combinability,
      targets: z.array(z.object({ kind: z.enum(['product', 'category']), name: z.string(), effect: z.enum(['include', 'exclude']) })),
      schedules: z.array(DiscountSchedule),
    })),
  })
  .meta({ id: 'MenuPreviewForReview' });

export const ReadMenuPreview = defineQuery({
  name: 'onboarding.menu.preview',
  summary: "The merchant's review link reads the newest revision of its own menu preview: the menu, its removals and its taxes by name.",
  input: z.object({ locationId: LocationId, previewId: MenuPreviewId }),
  output: MenuPreviewForReview,
  // The link's own principal, and only for the link's own preview (the server checks it against the link).
  permission: { session: 'review_link' },
  availability: 'cloud',
  cloudReason: PREVIEW_CLOUD,
  scope: 'location',
  errors: [OnboardingLinkInvalid, OnboardingPreviewNotFound],
  agent: { expose: false },
});

/** One entry of a preview's change list, as staff and their agent check it against what the merchant asked for. */
export const MenuChangeEntry = z
  .object({
    kind: MenuEntityKind,
    name: z.string(),
    change: z.enum(['new', 'changed', 'archive', 'delete']),
    /** Wire values: a price is `{ minor: "<integer>", currency }`. A new entity lists its price and choices only. */
    fields: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() })),
  })
  .meta({ id: 'MenuChangeEntry' });

export const ReadMenuChanges = defineQuery({
  name: 'onboarding.menu.changes',
  summary:
    "A preview's change list for Radish staff and the Desk agent: the newest revision's new, changed, archived and deleted items, and whether the merchant approved it. After the review call the agent checks it against the call's transcript and suggests what is missing; any fix is the staff member's draft, publish and approve.",
  input: z.object({ locationId: LocationId, previewId: MenuPreviewId }),
  output: z.object({
    revision: z.int().positive(), fingerprint: MenuDigest, approved: z.boolean(),
    /** The Dialpad call the merchant approved on, when the review link knew it: the transcript to check. */
    callId: z.string().nullable(),
    changes: z.array(MenuChangeEntry),
  }),
  permission: 'onboarding.menu.manage',
  availability: 'cloud',
  cloudReason: PREVIEW_CLOUD,
  scope: 'location',
  errors: [OnboardingPreviewNotFound],
  agent: { expose: true, surfaces: ['staff.onboarding'] },
});

/**
 * A photo QA picked for a store's menu, kept in the cloud's content-addressed store before any bundle names it, so the
 * bundle and its apply carry only its sha256. Exactly the catalog's image types and size cap, checked the same way.
 */
export const StageMenuImage = defineCommand({
  name: 'onboarding.menu.stageImage',
  summary: "Keep a menu photo in the cloud's image store for this account and answer its sha256, which a menu bundle then names for a product.",
  input: z.object({ locationId: LocationId, contentType: ProductImageType, data: ProductImageData }),
  output: z.object({ sha256: z.string(), contentType: ProductImageType, byteSize: z.int().positive() }),
  permission: 'onboarding.menu.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'Product images are kept in the cloud, as legacy kept them on its API server.',
  emits: [],
  errors: [ProductImageInvalid, ProductImageStoreUnavailable],
  dryRun: false,
  maxBodyBytes: PRODUCT_IMAGE_BODY_BYTES,
  agent: { expose: true, surfaces: ['staff.onboarding'] },
});

/** No photo for this item in the link's own preview (also the answer for any other preview, store or item). */
export const OnboardingImageNotFound = defineError({
  code: 'onboarding.image_not_found', httpStatus: 404, message: 'errors.onboarding.image_not_found', retryable: false,
  details: z.object({}),
});

/**
 * One item's photo as the merchant's review shows it: the photo the newest revision sets on that item, or the store's
 * own one it keeps. Only for the link's own store and preview, and only for an item that preview carries; the bytes
 * come from the content-addressed store, never from anything the link sends. The third and last operation a review
 * link may call (coordinator, 2026-10-16).
 */
export const ReadMenuPreviewImage = defineQuery({
  name: 'onboarding.menu.previewImage',
  summary: "The merchant's review link reads one item's photo in its own menu preview.",
  input: z.object({ locationId: LocationId, previewId: MenuPreviewId, productId: z.uuid() }),
  output: z.object({ contentType: ProductImageType, sha256: z.string(), data: z.string() }),
  permission: { session: 'review_link' },
  availability: 'cloud',
  cloudReason: PREVIEW_CLOUD,
  scope: 'location',
  cache: { maxAgeSeconds: 0, private: true, noStore: true },
  errors: [OnboardingImageNotFound, ProductImageStoreUnavailable],
  agent: { expose: false },
});

/**
 * The register layout of a location still being onboarded (ops plan 2026-10-17-launchpad-menu-build §4.4, Decided
 * 2026-10-18): the four Settings › Appearance keys at the location, which every register there starts from until it
 * saves its own. One command, so Launchpad's Claude sets the layout without holding the general settings.set.
 */
export const SetMenuLayout = defineCommand({
  name: 'onboarding.menu.setLayout',
  summary: "Set how the location's registers lay out the menu: the category menu on the left, at the top (paged or scrolling), or one page, and the grid's columns.",
  input: z.object({
    locationId: LocationId,
    /** `register.categories.menu_left` */
    menuLeft: z.boolean(),
    /** `register.categories.fixed_menu` */
    fixedMenu: z.boolean(),
    /** `register.categories.continuous_scroll` */
    continuousScroll: z.boolean(),
    /** `register.till.columns` */
    columns: z.int().min(4).max(6),
  }),
  output: z.object({ locationId: LocationId }),
  permission: 'onboarding.menu.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: "Launchpad's Claude reaches a location only through the Radish Broker, in the cloud.",
  emits: [],
  errors: [],
  dryRun: true,
  agent: {
    expose: true, surfaces: ['staff.onboarding'],
    description: 'Left category menu: menuLeft true, fixedMenu false. Top category menu: menuLeft false, fixedMenu true (continuousScroll scrolls instead of paging). Single page: both false.',
  },
});

/**
 * What Launchpad's Claude builds and edits a menu with (Andrew, 2026-10-18: "let's remove all the guardrails... no
 * approvals are necessary"): on the `staff.onboarding` surface, run by the Broker's /mcp/onboarding without an approval
 * card even when a command is high risk, and audited one entry per call. Every one still needs its own permission.
 */
export const LAUNCHPAD_MENU_OPERATIONS: readonly string[] = [
  'onboarding.menu.exportBundle', 'settings.effective',
  'catalog.categories.list', 'catalog.products.list', 'catalog.variants.list', 'catalog.modifierGroups.list', 'catalog.departments.list',
  'catalog.courses.list', 'catalog.taxProfiles.list', 'catalog.taxExemptions.list', 'catalog.ageRestrictions.list', 'catalog.printers.list',
  'catalog.diningOptions.list', 'catalog.discountRules.list',
  'catalog.upsertMenu', 'catalog.upsertCategory', 'catalog.deleteCategory', 'catalog.upsertProduct', 'catalog.deleteProduct', 'catalog.upsertVariant', 'catalog.deleteVariant',
  'catalog.upsertModifierGroup', 'catalog.deleteModifierGroup', 'catalog.upsertDepartment', 'catalog.deleteDepartment', 'catalog.upsertCourse',
  'catalog.deleteCourse', 'catalog.upsertTaxProfile', 'catalog.upsertTaxExemption', 'catalog.deleteTaxExemption', 'catalog.upsertAgeRestriction',
  'onboarding.menu.stageImage', 'catalog.setProductImage', 'onboarding.menu.setLayout', 'onboarding.menu.priceBeforeMarkup', 'onboarding.menu.scalePrices',
];

/**
 * The menu writes that count as Claude building a location's menu (B508): Launchpad's catalog and photo writes, never a
 * read, the layout or a staged photo not yet on an item.
 */
export const LAUNCHPAD_MENU_BUILD_WRITES: readonly string[] = LAUNCHPAD_MENU_OPERATIONS.filter((name) => name.startsWith('catalog.') && !name.endsWith('.list'));

/** Launchpad's Build it with Claude step (B508, review of #1414): when Claude last changed this location's menu. */
export const ReadMenuBuildHistory = defineQuery({
  name: 'onboarding.menu.buildHistory',
  summary:
    "When Launchpad's Claude last changed this location's menu: the newest accepted menu write it made through the Broker's onboarding endpoint, from the location's audit trail. Null when it never has. The layout, the back office and refused changes never count.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ lastChangedAt: IsoInstant.nullable() }),
  permission: 'onboarding.menu.manage',
  availability: 'cloud',
  cloudReason: "The cloud's audit trail holds every change Launchpad's Claude made through the Broker, which only the cloud serves.",
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

/** A markup in basis points as a canonical decimal string: "1750" is 17.5%. 0 to 10 000 (100%). */
const MarkupBasisPoints = z.string().regex(/^(0|[1-9][0-9]{0,4})$/).refine((v) => Number(v) <= 10_000, 'at most 10000 (100%)');

/**
 * Launchpad's menu build (ops plan 2026-10-17-launchpad-menu-build, Decided 2026-10-19, item 4; coordinator, review of
 * #1555): the store's own prices behind a delivery app's, worked out by core's rule, never by Claude. The markup is the
 * one the rep gave, or else the one core finds from items whose two prices are both known.
 */
export const ReadPriceBeforeMarkup = defineQuery({
  name: 'onboarding.menu.priceBeforeMarkup',
  summary:
    "The store's own prices behind delivery-app prices (DoorDash, Uber Eats, Grubhub), by core's rule: each delivery price × 10 000 ÷ (10 000 + the markup in basis points), rounded half up to a cent. The markup is `markupBasisPoints` when given; otherwise it is found from `matched` (items whose in-store and delivery prices are both known: the markup on their totals). Every matched item the markup doesn't explain (more than a cent off) comes back in `mismatches`, a given markup checked against them too, for the rep to check. It writes nothing.",
  input: z.object({
    locationId: LocationId,
    markupBasisPoints: MarkupBasisPoints.optional(),
    matched: z.array(z.object({ item: z.string().max(200), inStore: Money, delivery: Money })).max(400).optional(),
    prices: z.array(z.object({ item: z.string().max(200), delivery: Money })).min(1).max(400),
  }),
  output: z.object({
    markupBasisPoints: z.string(),
    markupFrom: z.enum(['given', 'matched']),
    prices: z.array(z.object({ item: z.string(), delivery: Money, inStore: Money })),
    /** Each matched item the markup doesn't explain (more than a cent off), whether the markup was given or found. */
    mismatches: z.array(z.object({ item: z.string(), inStore: Money, delivery: Money, worksOutTo: Money })),
  }),
  permission: 'onboarding.menu.manage',
  availability: 'cloud',
  cloudReason: "Launchpad's Claude reaches it through the cloud's Broker, the only way a menu build reaches Radish.",
  scope: 'location',
  errors: [CatalogInvalid],
  agent: {
    expose: true, surfaces: ['staff.onboarding'],
    description: "Work out the store's own prices from delivery-app prices and a markup (or from a few items whose two prices you know). Call it for every such price: never work a price out yourself.",
  },
});

/**
 * Launchpad's menu build (ops plan 2026-10-19-launchpad-bulk-mode, Decided 3; coordinator, review of #1577): items' and
 * sizes' prices changed by a percent, by core's one rule (scalePrice: to the nearest cent, half away from zero), so
 * Claude never works a price out itself and agrees with List mode's "Change prices by" to the cent. It writes nothing:
 * Claude writes the `after` prices with catalog.upsertMenu.
 */
export const ReadScaledPrices = defineQuery({
  name: 'onboarding.menu.scalePrices',
  summary:
    "Items' and sizes' prices at this location changed by a percent, by core's rule: each price × (1 + percent ÷ 100), rounded to the nearest cent with half away from zero ($4.25 + 5% = $4.46). `percent` is as typed: \"5\", \"-10\", \"7.5\" (two decimals at most, never below -100). Each comes back with its price before and after; one it won't change is listed in `skipped` with why (shared with the account, over $99,999.99, or not found). It writes nothing.",
  input: z.object({
    locationId: LocationId,
    percent: z.string().trim().min(1).max(12),
    productIds: z.array(ProductId).max(400).default([]),
    variantIds: z.array(VariantId).max(400).default([]),
  }),
  output: z.object({
    basisPoints: z.string(),
    items: z.array(z.object({ productId: z.uuid(), name: z.string(), before: Money, after: Money })),
    sizes: z.array(z.object({ variantId: z.uuid(), productId: z.uuid(), name: z.string(), before: Money, after: Money })),
    skipped: z.array(z.object({ id: z.uuid(), reason: z.enum(['shared', 'over_cap', 'not_found']) })),
  }),
  permission: 'onboarding.menu.manage',
  availability: 'cloud',
  cloudReason: "Launchpad's Claude reaches it through the cloud's Broker, the only way a menu build reaches Radish.",
  scope: 'location',
  errors: [CatalogInvalid],
  agent: {
    expose: true, surfaces: ['staff.onboarding'],
    description: 'Change prices by a percent ("raise these 5%"): pass the items and sizes and the percent, then write the after prices with catalog.upsertMenu. Call it for every such change: never work a price out yourself.',
  },
});

export const onboardingCommands = [PublishMenuPreview, RecordMenuApproval, RecordInPersonMenuApproval, SetMenuLayout, StageMenuImage] as const;
export const onboardingQueries = [ReadMenuPreview, ReadMenuChanges, ReadMenuPreviewImage, ReadMenuBuildHistory, ReadPriceBeforeMarkup, ReadScaledPrices] as const;
export const onboardingEvents = [MenuPreviewPublished, MenuApproved] as const;
